import {filter, sortBy} from 'ramda';

import type {EagColumnState} from '../../types/EagColumnState';
import {arraysShallowEqual} from '../../utils/arraysShallowEqual';
import type {EagGridState} from '../createEagGridStore';
import {useEagGridSelector} from '../useEagGridSelector';

const isVisible = (col: EagColumnState) => col.hidden !== true;
const byOrder = (col: EagColumnState) => col.order;

// Cached selector that returns visible columns ordered for rendering:
//   [pinnedLeft... | unpinned... | pinnedRight...]
// Each group preserves the original order field ascending.
//
// This DOM ordering is required for CSS `position: sticky` with `left`/`right`
// offsets to work correctly on HTML table cells — pinned-left columns must
// appear first in the DOM and pinned-right columns must appear last.
//
// Module-level cache: WeakMap keyed on state.columnStates so the cache is
// automatically invalidated whenever the array reference changes (i.e. on any
// store mutation).  `lastResult` enables a structural-equality short-circuit
// so that mutations affecting unrelated fields (e.g. width of a non-pinned
// column) do not produce a new array reference and cause unnecessary re-renders.
const cache = new WeakMap<EagColumnState[], EagColumnState[]>();
let lastResult: EagColumnState[] | null = null;

const selectOrderedVisibleColumns = (state: EagGridState): EagColumnState[] => {
  const cached = cache.get(state.columnStates);
  if (cached !== undefined) {
    return cached;
  }

  const visible = filter(isVisible, state.columnStates);

  const pinnedLeft: EagColumnState[] = [];
  const unpinned: EagColumnState[] = [];
  const pinnedRight: EagColumnState[] = [];

  for (const col of visible) {
    if (col.pinned === 'LEFT') {
      pinnedLeft.push(col);
    } else if (col.pinned === 'RIGHT') {
      pinnedRight.push(col);
    } else {
      unpinned.push(col);
    }
  }

  const result = [
    ...sortBy(byOrder, pinnedLeft),
    ...sortBy(byOrder, unpinned),
    ...sortBy(byOrder, pinnedRight),
  ];

  // Structural equality: if the grouped+sorted set hasn't changed (same elements
  // in same order), return the previous reference to prevent downstream re-renders.
  if (lastResult !== null && arraysShallowEqual(lastResult, result)) {
    cache.set(state.columnStates, lastResult);
    return lastResult;
  }

  lastResult = result;
  cache.set(state.columnStates, result);
  return result;
};

export function useOrderedVisibleColumns(): EagColumnState[] {
  return useEagGridSelector(selectOrderedVisibleColumns);
}
